import React, { useState } from 'react';
import { useFormData } from '../../index';
import { useTrans } from '../../../../services/i18n';
import { TextField } from '../../../../components/common/Inputs';
import AddressAutocomplete from '../../../../components/common/Inputs/AddressAutocomplete';
import { Grid } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Divider } from '../../../../components/common/Divider';
import { Button } from '../../../../components/common/Buttons';
import { ReactComponent as ArrowBackIcon } from '../../icons/arrow-left.svg';
import * as yup from 'yup';

const useStyles = makeStyles((theme) => ({
  labelDropdowns: {
    fontFamily: 'Lexend',
    marginBottom: '8px',
    color: '#4e5978',
    fontWeight: 500,
  },
  containerCard: {
    padding: '2.125rem 1.5rem 1rem',
  },
  containerFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.3rem 2.3rem 1.87rem 2.3rem',
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  backButton: {
    color: '#999999',
    fontSize: 18,
    fontFamily: 'Manrope',
    fontWeight: '500',
    wordWrap: 'break-word',
  },
  backButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  cancelNextButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const companyAddressSchema = yup.object().shape({
  addressLine1: yup.string().trim().required('Address Line 1 is required'),
  city: yup.string().trim().required('City is required'),
  addressLine2: yup.string().nullable().notRequired(),
  state: yup.string().trim().required('State is required'),
  zipCode: yup.string().trim().required('Zip Code is required'),
  country: yup.string().trim().required('Country is required'),
});

export default function CompanyAddress({ nextStep, previousStep, onCompletionChange, cancelButtonHandler }) {
  const { companyAddressData, setCompanyAddressData } = useFormData();
  const [errors, setErrors] = useState({});
  const { trans } = useTrans();
  const styles = useStyles();

  const validateField = (fieldName, value) => {
    try {
      yup.reach(companyAddressSchema, fieldName).validateSync(value);
      setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [fieldName]: err.message }));
    }
  };

  const handleBlur = (fieldName) => () => {
    validateField(fieldName, companyAddressData[fieldName]);
  };

  const handleFieldChange = (fieldName) => (e) => {
    const newValue = e.target.value;

    setCompanyAddressData((prev) => ({
      ...prev,
      [fieldName]: newValue,
    }));

    if (newValue.trim()) {
      setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
    }
  };

  const handleAddressChange = (val) => {
    setCompanyAddressData((prev) => ({
      ...prev,
      addressLine1: val,
    }));

    if (val.trim()) {
      setErrors((prev) => ({ ...prev, addressLine1: undefined }));
    }
  };

  const handleAddressSelect = (parsed) => {
    const updatedData = {
      ...companyAddressData,
      addressLine1: parsed.addressLine1 || companyAddressData.addressLine1,
      city: parsed.city || companyAddressData.city,
      state: parsed.state || companyAddressData.state,
      zipCode: parsed.zipCode || companyAddressData.zipCode,
      country: parsed.country || companyAddressData.country,
    };

    setCompanyAddressData(updatedData);

    ['addressLine1', 'city', 'state', 'zipCode', 'country'].forEach((field) => {
      if (updatedData[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    });
  };

  const handleNextClick = () => {
    try {
      companyAddressSchema.validateSync(companyAddressData, { abortEarly: false });
      setErrors({});
      onCompletionChange(true);
      nextStep();
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const errorMessages = {};
        error.inner.forEach((err) => {
          if (err.path && !errorMessages[err.path]) {
            errorMessages[err.path] = err.message;
          }
        });
        setErrors(errorMessages);
      }
      onCompletionChange(false);
    }
  };

  return (
    <React.Fragment>
      <Grid container spacing={3} className={styles.containerCard}>
        <div
          style={{
            width: '100%',
            height: '100%',
            paddingLeft: 40,
            paddingRight: 40,
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 24,
            display: 'inline-flex',
          }}
        >
          <div
            style={{
              textAlign: 'center',
              color: 'black',
              fontSize: 21,
              fontFamily: 'Manrope',
              fontWeight: '700',
              wordWrap: 'break-word',
            }}
          >
            Company Address
          </div>
          <div style={{ alignSelf: 'stretch', height: 1, background: '#D9D9D9' }} />
        </div>

        <Grid item sm={6} xs={12} px={0}>
          <AddressAutocomplete
            label={`${trans('organization-form-address-line-1')} *`}
            placeholder={trans('organization-form-address-line-1')}
            value={companyAddressData.addressLine1 || ''}
            onChange={handleAddressChange}
            onAddressSelect={handleAddressSelect}
            error={!!errors.addressLine1}
            helperText={errors.addressLine1}
            required
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <TextField
            type="text"
            label={`${trans('organization-form-city')} *`}
            placeholder={trans('organization-form-city')}
            value={companyAddressData.city || ''}
            onChange={handleFieldChange('city')}
            onBlur={handleBlur('city')}
            error={!!errors.city}
            helperText={errors.city}
            required
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <TextField
            type="text"
            label={trans('organization-form-address-line-2')}
            placeholder={trans('organization-form-address-line-2')}
            value={companyAddressData.addressLine2 || ''}
            onChange={handleFieldChange('addressLine2')}
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <TextField
            type="text"
            label={`${trans('organization-form-state')} *`}
            placeholder={trans('organization-form-state')}
            value={companyAddressData.state || ''}
            onChange={handleFieldChange('state')}
            onBlur={handleBlur('state')}
            error={!!errors.state}
            helperText={errors.state}
            required
          />
        </Grid>

        <Grid item sm={6} xs={12} px={0}>
          <TextField
            type="text"
            label={`${trans('organization-form-zip')} *`}
            placeholder={trans('organization-form-zip')}
            value={companyAddressData.zipCode || ''}
            onChange={handleFieldChange('zipCode')}
            onBlur={handleBlur('zipCode')}
            error={!!errors.zipCode}
            helperText={errors.zipCode}
            required
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <TextField
            type="text"
            label={`${trans('organization-form-country')} *`}
            placeholder={trans('organization-form-country')}
            value={companyAddressData.country || ''}
            onChange={handleFieldChange('country')}
            onBlur={handleBlur('country')}
            error={!!errors.country}
            helperText={errors.country}
            required
          />
        </Grid>
      </Grid>

      <Divider light spacing={1} />

      <Grid container className={styles.containerFooter}>
        <div className={styles.backButtonContainer}>
          <ArrowBackIcon />
          <div className={styles.backButton} onClick={previousStep}>
            {trans('organization-form-back-button')}
          </div>
        </div>
        <div className={styles.cancelNextButtonContainer}>
          <Button variant="contained" color="primary" onClick={cancelButtonHandler}>
            {trans('organization-form-cancel-button')}
          </Button>
          <Button variant="outlined" color="primary" onClick={handleNextClick} style={{ marginLeft: '10px' }}>
            {trans('organization-form-next-button')}
          </Button>
        </div>
      </Grid>
    </React.Fragment>
  );
}
